import type {
  BuyflowState,
  GetNextBuyflowStepRequest,
  NotificationPreferenceToggle,
  ProductWidget,
} from '@/lib/TypesOpenApi';
import {store as cartStore, shippingAddress} from '../../(state)/CartStore';
import {store as notificationsStore} from '../../(state)/NotificationPreferencesStore';
import {store as phoneQuestionnaireStore} from '../../(state)/PhoneQuestionnaireStore';
import {store as preconfigStore} from '../../(state)/PreconfigStore';
import {store as schedulingStore} from '../../(state)/ScheduleStore';
import {store as signInStore} from '../../(state)/SignInStore';
import {store as verifyStore} from '../../(state)/VerifyStore';
import type {Step} from '../StepsRouting';

import {isCiamEnabled} from '@gfiber/pixel-party';
import {store as streamingStore} from '../../(state)/StreamingStore';
import {store as wifiSetupStore} from '../../(state)/WifiSetupStore';
import {buyflowStore} from '../buyflowStore';
import type {AppState} from './AppState';
import {buildSelectedProducts} from './buildSelectedProducts';

export interface StateOptions {
  currentStep: Step;
  refresh: boolean;
  isBasic: boolean;
}

export const collectAppState = ({
  currentStep,
  refresh,
  isBasic,
}: StateOptions): AppState => {
  if (currentStep.name === 'PHONE') {
    preconfigStore.PHONE_PRODUCT = true;
  }

  const appState: AppState = {
    refresh,
    isBasic,
    currentStep: currentStep.name,
    buyflowState: buyflowStore.state,
    isPhonePreselected: !!preconfigStore.PHONE_PRODUCT,
    isStreamingPreselected: !!preconfigStore.STREAMING_PRODUCT,
    selectedTimeSlot: schedulingStore.timeSlot,
    productDeliveryChoice: cartStore.deliveryOption.productDeliveryChoice,
    products: {
      internet: cartStore.internet.productWidget,
      internetAddons: cartStore.internet.internetAddons
        ? cartStore.internet.internetAddons.filter(
            addon => addon.selected === true,
          )
        : undefined,
      phone: cartStore.phone.selected,
      battery: cartStore.battery.selected,
      staticIp: cartStore.staticIp.selected,
      streaming: streamingStore.isEnabled
        ? [streamingStore.selected!, ...cartStore.streaming.products]
        : undefined,
      points: cartStore.points.all,
      additionalItems: cartStore.additionalItems,
    },
    pickupAddress:
      cartStore.deliveryOption.productDeliveryChoice !== 'PICKUP'
        ? undefined
        : {
            physicalAddress: cartStore.deliveryOption.address,
            gfiberStoreName: cartStore.deliveryOption.gfiberStoreName,
          },
    shippingAddress: shippingAddress.shippingAddressInfo,
    questionnaireOptions: phoneQuestionnaireStore.selected,
    notificationPreferences: notificationsStore.enabled,
    userInfo: {
      phoneNumber: verifyStore.phone,
      isMobileNumber: verifyStore.isMobile,
    },
    wifiConfigDetails: {
      networkName: wifiSetupStore.networkName,
      networkPassword: wifiSetupStore.networkPassword,
    },
    userDetails: {
      firstName: verifyStore.firstName,
      lastName: verifyStore.lastName,
      businessContactFirstName: verifyStore.businessContactFirstName,
      businessContactLastName: verifyStore.businessContactLastName,
      businessName: verifyStore.businessName,
    },
  };

  if (
    isCiamEnabled() &&
    currentStep.name === 'SIGN_IN' &&
    signInStore.enteredUserEmail
  ) {
    appState.userDetails.email = signInStore.enteredUserEmail;
  }

  return appState;
};

export const mapState2Api = (
  globalAddressId: string,
  options: StateOptions,
): GetNextBuyflowStepRequest => {
  const state = collectAppState(options);
  const {currentStep} = state;
  // Integrate changes with buyflowState that is provided by backend.
  const buyflowState =
    state.buyflowState ||
    ({
      serviceAddressInfo: {globalAddressId},
    } as BuyflowState); // buyflowState can not exits in case of reloading page in a middle of the flow

  // override currentStep, as it can be incrrect due to reloads and back/forth navigation
  buyflowState.currentStep = currentStep;

  const {
    questionnaireOptions,
    productDeliveryChoice,
    selectedTimeSlot,
    pickupAddress,
    shippingAddress,
    userInfo,
    wifiConfigDetails,
    userDetails,
    isBasic,
  } = state;
  buyflowState.userSelection = {
    ...buyflowState.userSelection,
    ...{
      selectedProducts: buildSelectedProducts(
        state.products,
      ) as ProductWidget[],
      phoneSelections: {
        isPhoneSelected: state.isPhonePreselected,
        questionnaireOptions,
      },
      productDeliveryChoice,
      isStreamingSelected: state.isStreamingPreselected,
      selectedTimeSlot,
      pickupAddress,
      shippingAddress,
      notificationPreferences:
        state.notificationPreferences.map<NotificationPreferenceToggle>(
          preferenceType => ({preferenceType, selected: true}),
        ),
      userInfo,
      wifiConfigDetails,
      isBasic,
    },
  };

  buyflowState.userDetails = {
    ...buyflowState.userDetails,
    ...{
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      businessContactFirstName: userDetails.businessContactFirstName,
      businessContactLastName: userDetails.businessContactLastName,
      businessName: userDetails.businessName,
      ...(userDetails.email ? {email: userDetails.email} : {}),
    },
  };

  const {refresh} = state;
  return {
    refresh,
    buyflowState,
  };
};
